import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Card,
    Chip,
    Container,
    Grid,
    Stack,
    Typography,
} from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DataObjectRoundedIcon from '@mui/icons-material/DataObjectRounded';
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import SyncAltRoundedIcon from '@mui/icons-material/SyncAltRounded';
import ContactDialog from './ContactDialog';
import CalendarDialog from './CalendarDialog';
import { applyRouteSeo } from '../helpers/seoHelper';

const solutions = [
    {
        icon: <AutoAwesomeRoundedIcon />,
        eyebrow: 'AI & automation',
        title: 'Put repetitive work on autopilot',
        description:
            'Connect the tools you already use and automate handoffs, follow-ups, document processing, and routine decisions.',
        points: ['Workflow automation', 'AI assistants', 'System integrations'],
    },
    {
        icon: <DataObjectRoundedIcon />,
        eyebrow: 'Custom software',
        title: 'Build tools around how you work',
        description:
            'Replace scattered spreadsheets and manual processes with focused internal tools your team can actually use.',
        points: ['Internal platforms', 'Customer portals', 'Dashboards & reporting'],
    },
    {
        icon: <LanguageRoundedIcon />,
        eyebrow: 'Web experiences',
        title: 'Turn your website into a business asset',
        description:
            'Launch a clear, fast, mobile-first website designed to build trust and convert visitors into enquiries.',
        points: ['Business websites', 'Landing pages', 'Conversion-focused UX'],
    },
];

const process = [
    { number: '01', title: 'Discover', text: 'We map the problem, the people involved, and the outcome worth solving.' },
    { number: '02', title: 'Design', text: 'You see the proposed experience and delivery plan before development begins.' },
    { number: '03', title: 'Build', text: 'We develop in focused stages, with regular demos and practical feedback loops.' },
    { number: '04', title: 'Launch', text: 'We deploy, document, and support the solution as your business grows.' },
];

const Home = () => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [calendarDialogOpen, setCalendarDialogOpen] = useState(false);

    useEffect(() => applyRouteSeo('/'), []);

    return (
        <Box sx={{ overflow: 'hidden', bgcolor: 'background.default' }}>
            <Box
                component="section"
                sx={{
                    position: 'relative',
                    bgcolor: '#F7FBFC',
                    pt: { xs: 8, md: 11 },
                    pb: { xs: 9, md: 12 },
                    borderBottom: '1px solid rgba(16, 42, 58, 0.08)',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        width: 540,
                        height: 540,
                        borderRadius: '50%',
                        top: -260,
                        right: -170,
                        background: 'radial-gradient(circle, rgba(32,184,181,0.13) 0%, rgba(32,184,181,0) 70%)',
                        pointerEvents: 'none',
                    },
                }}
            >
                <Container maxWidth="lg" sx={{ position: 'relative' }}>
                    <Grid container spacing={{ xs: 7, md: 8 }} alignItems="center">
                        <Grid item xs={12} md={7}>
                            <Chip
                                label="AI automation · Custom software · Websites"
                                sx={{
                                    mb: 3,
                                    bgcolor: 'secondary.light',
                                    color: 'secondary.dark',
                                    fontWeight: 750,
                                    border: '1px solid rgba(32,184,181,0.18)',
                                }}
                            />
                            <Typography
                                component="h1"
                                sx={{
                                    maxWidth: 760,
                                    fontSize: { xs: '2.65rem', sm: '3.7rem', md: '4.6rem' },
                                    lineHeight: { xs: 1.08, md: 1.02 },
                                    fontWeight: 850,
                                    letterSpacing: '-0.055em',
                                    color: 'text.primary',
                                }}
                            >
                                Practical digital solutions,{' '}
                                <Box component="span" sx={{ color: 'primary.main' }}>built around your business.</Box>
                            </Typography>
                            <Typography
                                sx={{
                                    mt: 3,
                                    maxWidth: 650,
                                    fontSize: { xs: '1.08rem', md: '1.25rem' },
                                    lineHeight: 1.7,
                                    color: 'text.secondary',
                                }}
                            >
                                DevCraft helps growing businesses automate repetitive work, create focused software,
                                and launch websites that turn attention into action.
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 4.5 }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    endIcon={<ArrowForwardRoundedIcon />}
                                    onClick={() => setCalendarDialogOpen(true)}
                                    sx={{ px: 3.2, py: 1.45, fontSize: '1rem', bgcolor: 'primary.main' }}
                                >
                                    Book a discovery call
                                </Button>
                                <Button
                                    component="a"
                                    href="#solutions"
                                    variant="outlined"
                                    size="large"
                                    sx={{ px: 3.2, py: 1.45, fontSize: '1rem', borderColor: 'rgba(7,90,140,0.28)' }}
                                >
                                    Explore solutions
                                </Button>
                            </Stack>
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={{ xs: 1, sm: 2.5 }}
                                sx={{ mt: 4.5, color: 'text.secondary' }}
                            >
                                {['Clear scope', 'Practical delivery', 'Human support'].map((item) => (
                                    <Stack key={item} direction="row" spacing={0.8} alignItems="center">
                                        <CheckCircleRoundedIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
                                        <Typography variant="body2" fontWeight={650}>{item}</Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        </Grid>

                        <Grid item xs={12} md={5}>
                            <Box
                                sx={{
                                    bgcolor: '#082F49',
                                    color: 'white',
                                    borderRadius: { xs: 3, md: 4 },
                                    p: { xs: 2.5, sm: 3.5 },
                                    boxShadow: '0 28px 80px rgba(8, 47, 73, 0.18)',
                                    border: '1px solid rgba(255,255,255,0.08)',
                                }}
                            >
                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                                    <Box>
                                        <Typography variant="overline" sx={{ color: '#84E4E1', letterSpacing: 1.4, fontWeight: 800 }}>
                                            Example workflow
                                        </Typography>
                                        <Typography fontWeight={750}>New enquiry → ready to act</Typography>
                                    </Box>
                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#43D4CE', boxShadow: '0 0 0 6px rgba(67,212,206,0.1)' }} />
                                </Stack>

                                {[
                                    { label: 'Customer enquiry received', detail: 'Website · Email · WhatsApp', icon: <LanguageRoundedIcon /> },
                                    { label: 'AI organises the request', detail: 'Classify · Extract · Prioritise', icon: <AutoAwesomeRoundedIcon /> },
                                    { label: 'Your team gets the next step', detail: 'CRM updated · Follow-up drafted', icon: <SyncAltRoundedIcon /> },
                                ].map((item, index) => (
                                    <React.Fragment key={item.label}>
                                        <Stack
                                            direction="row"
                                            spacing={1.6}
                                            alignItems="center"
                                            sx={{
                                                p: 2,
                                                borderRadius: 2.2,
                                                bgcolor: index === 1 ? 'rgba(32,184,181,0.16)' : 'rgba(255,255,255,0.06)',
                                                border: index === 1 ? '1px solid rgba(69,218,211,0.34)' : '1px solid rgba(255,255,255,0.08)',
                                            }}
                                        >
                                            <Box sx={{ display: 'grid', placeItems: 'center', width: 42, height: 42, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.09)', color: '#75DDD9' }}>
                                                {item.icon}
                                            </Box>
                                            <Box>
                                                <Typography fontWeight={720} fontSize="0.94rem">{item.label}</Typography>
                                                <Typography fontSize="0.78rem" sx={{ color: 'rgba(255,255,255,0.62)', mt: 0.3 }}>{item.detail}</Typography>
                                            </Box>
                                        </Stack>
                                        {index < 2 && (
                                            <Box sx={{ height: 18, width: 2, bgcolor: 'rgba(117,221,217,0.4)', ml: 4.5 }} />
                                        )}
                                    </React.Fragment>
                                ))}
                                <Stack direction="row" spacing={2} sx={{ mt: 3, pt: 2.5, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                                    <ScheduleRoundedIcon sx={{ color: '#75DDD9' }} />
                                    <Box>
                                        <Typography fontWeight={700} fontSize="0.9rem">Less admin. Faster response.</Typography>
                                        <Typography fontSize="0.78rem" sx={{ color: 'rgba(255,255,255,0.62)' }}>Designed around your actual process.</Typography>
                                    </Box>
                                </Stack>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            <Box component="section" id="solutions" sx={{ py: { xs: 9, md: 13 }, bgcolor: 'white' }}>
                <Container maxWidth="lg">
                    <Box sx={{ maxWidth: 680, mb: { xs: 5, md: 7 } }}>
                        <Typography variant="overline" sx={{ color: 'secondary.dark', fontWeight: 800, letterSpacing: 1.4 }}>
                            What we build
                        </Typography>
                        <Typography component="h2" sx={{ mt: 1, fontSize: { xs: '2.1rem', md: '3.2rem' }, lineHeight: 1.12, fontWeight: 820, letterSpacing: '-0.045em' }}>
                            Technology that solves a real business problem.
                        </Typography>
                        <Typography sx={{ mt: 2, color: 'text.secondary', fontSize: '1.08rem', lineHeight: 1.7 }}>
                            No unnecessary complexity. We choose the right solution for the outcome you need.
                        </Typography>
                    </Box>
                    <Grid container spacing={3}>
                        {solutions.map((solution) => (
                            <Grid item xs={12} md={4} key={solution.title}>
                                <Card
                                    elevation={0}
                                    sx={{
                                        height: '100%',
                                        p: { xs: 3, md: 3.5 },
                                        border: '1px solid rgba(16,42,58,0.1)',
                                        borderRadius: 3,
                                        transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
                                        '&:hover': {
                                            transform: 'translateY(-5px)',
                                            boxShadow: '0 22px 48px rgba(16,42,58,0.09)',
                                            borderColor: 'rgba(32,184,181,0.45)',
                                        },
                                    }}
                                >
                                    <Box sx={{ width: 48, height: 48, display: 'grid', placeItems: 'center', color: 'primary.main', bgcolor: 'secondary.light', borderRadius: 2, mb: 3 }}>
                                        {solution.icon}
                                    </Box>
                                    <Typography variant="overline" sx={{ color: 'secondary.dark', fontWeight: 800, letterSpacing: 1.1 }}>
                                        {solution.eyebrow}
                                    </Typography>
                                    <Typography component="h3" sx={{ mt: 0.7, fontSize: '1.45rem', lineHeight: 1.25, fontWeight: 780 }}>
                                        {solution.title}
                                    </Typography>
                                    <Typography sx={{ mt: 1.6, color: 'text.secondary', lineHeight: 1.7 }}>
                                        {solution.description}
                                    </Typography>
                                    <Stack spacing={1.1} sx={{ mt: 3 }}>
                                        {solution.points.map((point) => (
                                            <Stack key={point} direction="row" spacing={1} alignItems="center">
                                                <CheckCircleRoundedIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
                                                <Typography variant="body2" fontWeight={650}>{point}</Typography>
                                            </Stack>
                                        ))}
                                    </Stack>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            <Box component="section" sx={{ py: { xs: 9, md: 12 }, bgcolor: '#EEF7F8' }}>
                <Container maxWidth="lg">
                    <Grid container spacing={{ xs: 6, md: 10 }} alignItems="center">
                        <Grid item xs={12} md={5}>
                            <Typography variant="overline" sx={{ color: 'secondary.dark', fontWeight: 800, letterSpacing: 1.4 }}>
                                How we work
                            </Typography>
                            <Typography component="h2" sx={{ mt: 1, fontSize: { xs: '2.1rem', md: '3rem' }, lineHeight: 1.12, fontWeight: 820, letterSpacing: '-0.045em' }}>
                                Clear from the first conversation to launch.
                            </Typography>
                            <Typography sx={{ mt: 2.5, color: 'text.secondary', fontSize: '1.06rem', lineHeight: 1.75 }}>
                                You always know what we are solving, what comes next, and how the work connects to your business goal.
                            </Typography>
                            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mt: 3.5 }}>
                                <SecurityRoundedIcon sx={{ color: 'primary.main' }} />
                                <Typography fontWeight={700}>Thoughtful about security, ownership, and maintainability.</Typography>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <Grid container spacing={2}>
                                {process.map((step) => (
                                    <Grid item xs={12} sm={6} key={step.number}>
                                        <Box sx={{ height: '100%', bgcolor: 'white', p: 3, borderRadius: 2.5, border: '1px solid rgba(16,42,58,0.08)' }}>
                                            <Typography sx={{ color: 'secondary.main', fontWeight: 850, fontSize: '0.85rem', letterSpacing: 1.1 }}>{step.number}</Typography>
                                            <Typography component="h3" sx={{ mt: 1.2, fontSize: '1.25rem', fontWeight: 780 }}>{step.title}</Typography>
                                            <Typography sx={{ mt: 1, color: 'text.secondary', lineHeight: 1.65, fontSize: '0.94rem' }}>{step.text}</Typography>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            <Box component="section" sx={{ py: { xs: 9, md: 12 }, bgcolor: '#072F49', color: 'white' }}>
                <Container maxWidth="md" sx={{ textAlign: 'center' }}>
                    <Typography variant="overline" sx={{ color: '#77DEDB', fontWeight: 800, letterSpacing: 1.5 }}>
                        Start with the problem
                    </Typography>
                    <Typography component="h2" sx={{ mt: 1.5, fontSize: { xs: '2.2rem', md: '3.5rem' }, lineHeight: 1.1, fontWeight: 820, letterSpacing: '-0.045em' }}>
                        Let’s find the simplest way to move your business forward.
                    </Typography>
                    <Typography sx={{ mt: 2.5, mx: 'auto', maxWidth: 620, color: 'rgba(255,255,255,0.72)', fontSize: '1.08rem', lineHeight: 1.7 }}>
                        Tell us what is taking too much time, creating friction, or holding back growth. We’ll help you shape the right next step.
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center" sx={{ mt: 4 }}>
                        <Button
                            variant="contained"
                            size="large"
                            endIcon={<ArrowForwardRoundedIcon />}
                            onClick={() => setCalendarDialogOpen(true)}
                            sx={{ px: 3.4, py: 1.45, bgcolor: 'secondary.main', color: '#062E48', '&:hover': { bgcolor: '#64D6D2' } }}
                        >
                            Book a discovery call
                        </Button>
                        <Button
                            variant="outlined"
                            size="large"
                            onClick={() => setDialogOpen(true)}
                            sx={{ px: 3.4, py: 1.45, color: 'white', borderColor: 'rgba(255,255,255,0.35)', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.05)' } }}
                        >
                            Send us a message
                        </Button>
                    </Stack>
                    <Typography sx={{ mt: 2.5, color: 'rgba(255,255,255,0.5)', fontSize: '0.86rem' }}>
                        A focused first conversation. No hard sell.
                    </Typography>
                </Container>
            </Box>

            <ContactDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                serviceTitle="General project enquiry"
            />
            <CalendarDialog
                open={calendarDialogOpen}
                onClose={() => setCalendarDialogOpen(false)}
            />
        </Box>
    );
};

export default Home;
